import React, { useEffect, useState, useRef } from "react";
import { APIProvider, Map, useMap } from "@vis.gl/react-google-maps";
import Core, { SimpleLatLng } from "./Core";
import { API_KEY } from "../../config/maps";
import { PolygonHoleProvider } from "./contexts/PolygonHoleContext";
import { DataLayerProvider } from "./contexts/DataLayerContext";
import { SurfaceType } from "./DataAndMap";
import ToolbarStateProvider from "./contexts/ToolbarStateContext";

const SiteMapEditor: React.FC<{
  children?: React.ReactNode;
  location: SimpleLatLng;
  surfaceTypes: SurfaceType[];
  showControls: boolean;
  fullScreen?: boolean;
  onMapReady?: (map: google.maps.Map) => void;
  zoom?: number;
  center?: SimpleLatLng;
}> = ({
  children,
  location,
  surfaceTypes,
  showControls,
  fullScreen = false,
  onMapReady = undefined,
  zoom,
  center,
}) => {
  const [tilesLoaded, setTilesLoaded] = useState(false);
  const [mapIdle, setMapIdle] = useState(false);

  // we move most work down a layer as it then gives our APIProvider
  // context system a chance to work out how to share the map internally
  // when we had this up a layer things didn't work
  return (
    <>
      <APIProvider apiKey={API_KEY}>
        <PolygonHoleProvider>
          <Map
            id="property-site-map"
            mapId={"property-site-map"}
            mapTypeId="satellite"
            style={{
              width: "100%",
              height: fullScreen ? "100vh" : "calc(100vh - 160px)",
            }}
            colorScheme="FOLLOW_SYSTEM"
            defaultCenter={center ?? location}
            defaultTilt={0}
            defaultZoom={zoom ?? 17}
            gestureHandling={"greedy"}
            reuseMaps={true}
            controlSize={34}
            onTilesLoaded={() => {
              setTilesLoaded(true);
            }}
            onIdle={() => {
              setMapIdle(true);
            }}
            disableDefaultUI={!showControls}
            fullscreenControl={false}
            fullscreenControlOptions={{
              position: 9, // google.maps.ControlPosition.BOTTOM_RIGHT,
            }}
            mapTypeControlOptions={{
              position: 3, // google.maps.ControlPosition.TOP_RIGHT,
            }}
            cameraControlOptions={{
              position: 6, // google.maps.ControlPosition.LEFT_BOTTOM,
            }}
          >
            <MapReadyWatcher
              onMapReady={onMapReady}
              tilesLoaded={tilesLoaded}
              mapIdle={mapIdle}
            />
            <ToolbarStateProvider>
              <DataLayerProvider>
                <Core
                  location={location}
                  showControls={showControls}
                  surfaceTypes={surfaceTypes}
                  centerOnContent={center == null}
                />
                {children}
              </DataLayerProvider>
            </ToolbarStateProvider>
          </Map>
        </PolygonHoleProvider>
      </APIProvider>

      {/* here to help with Chrome rendering, if we need more control
        we'll need to pass a prop down to call onTilesLoaded instead
      */}
    </>
  );
};

/**
 * Small component to watch the maps and tiles and fire the event handler
 * when everything is ready.
 * Tracks active tile network traffic to detect stalls, and triggers an in-map
 * self-healing nudge if tile requests hang for >4.5s.
 */
const MapReadyWatcher: React.FC<{
  onMapReady?: (map: google.maps.Map) => void;
  tilesLoaded: boolean;
  mapIdle: boolean;
}> = ({ onMapReady = undefined, tilesLoaded, mapIdle }) => {
  const map = useMap();
  const lastTileActivityRef = useRef(Date.now());
  const mountTimeRef = useRef(Date.now());
  const nudgedRef = useRef(false);
  const readyFiredRef = useRef(false);
  const observedTileActivityRef = useRef(false);
  const panTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Monitor tile resource requests to distinguish active downloading from settled state
  useEffect(() => {
    if (typeof window === "undefined" || !("PerformanceObserver" in window))
      return;
    try {
      const observer = new PerformanceObserver((list) => {
        for (const entry of list.getEntries()) {
          const name = entry.name;
          if (
            name.includes("khms") ||
            name.includes("vt?pb=") ||
            name.includes("kh?v=") ||
            name.includes("googleapis.com/maps")
          ) {
            lastTileActivityRef.current = Date.now();
            observedTileActivityRef.current = true;
          }
        }
      });
      observer.observe({ entryTypes: ["resource"] });
      return () => observer.disconnect();
    } catch {
      // PerformanceObserver fallback
    }
  }, []);

  const triggerReady = React.useCallback(() => {
    if (readyFiredRef.current) return;
    readyFiredRef.current = true;
    if (typeof onMapReady === "function" && map) {
      onMapReady(map);
    }
  }, [onMapReady, map]);

  useEffect(() => {
    // 1. Ideal case: both tilesloaded and mapIdle have fired
    if (tilesLoaded && mapIdle) {
      triggerReady();
      return;
    }

    if (!map) return;

    // 2. Fallback watchdog: if map is idle and tile downloads have settled
    // (or after 6s total if map is idle), resolve safely so we don't hang
    const interval = setInterval(() => {
      const timeSinceActivity = Date.now() - lastTileActivityRef.current;
      const totalElapsed = Date.now() - mountTimeRef.current;

      // If map is idle and tile activity has ceased for >= 1.5s after tiles were observed, all visible imagery is drawn!
      if (
        mapIdle &&
        observedTileActivityRef.current &&
        timeSinceActivity >= 1500 &&
        totalElapsed >= 2000
      ) {
        triggerReady();
        return;
      }

      // Safety net: if map has been idle for >= 6s total, declare ready
      if (mapIdle && totalElapsed >= 6000) {
        triggerReady();
        return;
      }

      // If tiles stalled during initial download before idle, nudge once at 4.5s
      if (timeSinceActivity >= 4500 && !nudgedRef.current && !mapIdle) {
        nudgedRef.current = true;
        lastTileActivityRef.current = Date.now();
        google.maps.event.trigger(map, "resize");
        map.panBy(1, 0);
        panTimeoutRef.current = setTimeout(() => map.panBy(-1, 0), 100);
      }
    }, 500);

    return () => {
      clearInterval(interval);
      if (panTimeoutRef.current) {
        clearTimeout(panTimeoutRef.current);
      }
    };
  }, [tilesLoaded, mapIdle, map, triggerReady]);

  return null;
};

export default SiteMapEditor;
